import { Image, Link, Text, View } from '@react-pdf/renderer'
import { tw } from '~/components/pdfs'
import { type Listing, type ListingBroker, type ModelCompany } from '~/models'
import {
  fmtName,
  fmtPhoneNumber,
  getBrokerMemberships,
  getPhoneHref,
  transformImageUrl,
} from '~/utils'

export default function PdfListingBrokers({ listing }: { listing: Listing }) {
  const brokersByCompany: {
    company: ModelCompany
    brokers: ListingBroker[]
  }[] = [
    {
      company: listing.company,
      brokers: [],
    },
  ]

  listing.brokers.forEach((broker) => {
    if (broker.status === 'inactive') return
    const companyIndex = brokersByCompany.findIndex(
      (item) => item.company._id === broker.company._id
    )
    if (companyIndex === -1) {
      brokersByCompany.push({
        company: broker.company,
        brokers: [broker],
      })
    } else {
      brokersByCompany[companyIndex].brokers.push(broker)
    }
  })

  return (
    <>
      <View style={tw('flex flex-col')}>
        {brokersByCompany.map((item) => (
          <CompanyBrokers
            key={item.company._id}
            company={item.company}
            brokers={item.brokers}
          />
        ))}
      </View>
    </>
  )
}

function CompanyBrokers({
  company,
  brokers,
}: {
  company: ModelCompany
  brokers: ListingBroker[]
}) {
  return (
    <View style={tw('mt-6')} break>
      <Text style={tw('font-heading mb-4 text-lg leading-none')}>
        {company.name}
      </Text>
      {brokers.map((broker) => {
        if (broker.status === 'inactive') {
          return (
            <View key={broker._id} style={tw('mb-4')}>
              <Text style={tw('mb-1 text-base font-medium')}>
                {fmtName(broker.firstName, broker.middleName, broker.lastName)}
              </Text>
              <Text style={tw('text-sm')}>
                Broker no longer with {company.name}
              </Text>
            </View>
          )
        }
        return (
          <View key={broker._id} style={tw('mb-4 flex flex-col gap-2')}>
            <View style={tw('flex flex-row items-start gap-4')}>
              {broker.meta?.photo ? (
                <Image
                  style={tw('w-12 rounded-full')}
                  src={transformImageUrl(broker.meta?.photo?.thumbUrl)}
                />
              ) : (
                <View
                  style={tw(
                    'flex h-12 w-12 flex-row items-center justify-center rounded-full bg-gray-200'
                  )}>
                  <Text style={tw('mt-px text-base font-medium')}>
                    {[broker.firstName.slice(0, 1), broker.lastName.slice(0, 1)]
                      .join('')
                      .toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={tw('flex flex-col gap-1')}>
                <View style={tw('flex flex-row')}>
                  <Text style={tw('text-base font-medium')}>
                    {fmtName(
                      broker.firstName,
                      broker.middleName,
                      broker.lastName
                    )}
                  </Text>
                  {broker.meta?.memberships && (
                    <Text style={tw('ml-2 text-base text-muted-foreground')}>
                      {getBrokerMemberships(broker)}
                    </Text>
                  )}
                </View>
                {broker.meta?.title && (
                  <Text style={tw('text-sm text-muted')}>
                    {broker.meta.title}
                  </Text>
                )}
                {broker.email && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>E:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={`mailto:${broker.email}`}>
                      {broker.email}
                    </Link>
                  </View>
                )}
                {broker.meta?.office && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>O:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={getPhoneHref(
                        broker.meta?.office,
                        broker.meta?.officeExtension
                      )}>
                      {fmtPhoneNumber(
                        broker.meta.office,
                        broker.meta.officeExtension
                      )}
                    </Link>
                  </View>
                )}
                {broker.meta?.mobile && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>M:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={`tel:${broker.meta?.mobile}`}>
                      {fmtPhoneNumber(broker.meta.mobile)}
                    </Link>
                  </View>
                )}
              </View>
            </View>
          </View>
        )
      })}
    </View>
  )
}
